import dayjs from "dayjs";
import {
  Company,
  CompanySpecificProduct,
  Contract,
  ContractLight,
  ContractTradeType,
  CurrentCompany,
  PlatformService,
} from "@/types";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import PriceWeightUnitEnum from "@/constants/enums/PriceWeightUnitEnum";
import PlatformServiceUnitEnum from "@/constants/enums/PlatformServiceUnitEnum";

export const getContractTradeType = (contract: Contract | ContractLight, company: CurrentCompany | undefined) => {
  if (!company) return ContractTradeTypeEnum.BUY;
  return contract.contractSellers.some((seller) => seller.id === company.companyId)
    ? ContractTradeTypeEnum.SELL
    : ContractTradeTypeEnum.BUY;
};

export const getTradingPartnerCompany = (contract: Contract | ContractLight, currentCompanyId: number | undefined) => {
  if (!currentCompanyId) return null;
  return contract?.contractSellers.some((seller) => seller.id === currentCompanyId)
    ? contract?.contractBuyers[0]
    : contract?.contractSellers[0];
};

// The name tells contracts on the same product apart. Contracts saved without one fall back to the product. The
// `?.` covers a missing name, which the type doesn't admit but a single bad row must not crash a whole list over.
export const getContractDisplayName = (contract: Contract | ContractLight) =>
  contract.name?.trim() || contract.contractProduct.companySpecificProduct.companySpecificProductName;

// Every buyer and seller, our own company included — unlike getTradingPartnerCompany, this does not assume a
// single counterparty.
export const getContractParties = (contract: Contract | ContractLight | null | undefined): Company[] => [
  ...(contract?.contractBuyers ?? []),
  ...(contract?.contractSellers ?? []),
];

export const contractsUIConfig = (contractTradeType: ContractTradeType) =>
  ({
    buy: {
      iconName: "purchaseIndicatorLarge",
      tableIconName: "purchaseIndicatorSquare",
      whiteIconName: "saleIndicator-white",
      progressColor: "green",
      companyIcon: "arrowPurchaseBlack",
      partnerIcon: "arrowSaleBlack",
      createOrderModalHeader: "common:purchaseOrder",
      tablePartnerIcon: "arrowGrayUp",
    },
    sell: {
      iconName: "salesIndicatorLarge",
      tableIconName: "saleIndicatorSquare",
      whiteIconName: "purchaseIndicator-white",
      progressColor: "red",
      companyIcon: "arrowSaleBlack",
      partnerIcon: "arrowPurchaseBlack",
      createOrderModalHeader: "common:salesOrder",
      tablePartnerIcon: "arrowGrayDown",
    },
  })[contractTradeType];

export const getContractCardDetails = (contract?: Contract | ContractLight | null) => {
  const sellers = contract?.contractSellers ?? [];
  const buyers = contract?.contractBuyers ?? [];
  const approvals = contract?.approvals ?? [];
  const contractDate = contract?.contractDate;

  return {
    sellerName: sellers[0]?.companyName ?? "",
    buyerName: buyers[0]?.companyName ?? "",
    allApproved:
      sellers.length > 0 &&
      buyers.length > 0 &&
      sellers.every((seller) => approvals.some((approval) => approval.approvalCompany === seller.id)) &&
      buyers.every((buyer) => approvals.some((approval) => approval.approvalCompany === buyer.id)),
    createdDate: dayjs(contractDate).isValid() ? dayjs(contractDate).toDate() : "",
    notApproved: sellers
      .filter((seller) => !approvals.some((approval) => approval.approvalCompany === seller.id))
      .concat(buyers.filter((buyer) => !approvals.some((approval) => approval.approvalCompany === buyer.id))),
  };
};

/**
 * The agreed price per price unit from the company's own side. The buyer's price includes the platform fees,
 * so each party sees the price they pay or receive. Negative means the payment goes the other way.
 */
export const getAgreedPriceForCompany = (contract: Contract | ContractLight, company: CurrentCompany | undefined) => {
  const { agreedBuyerPricePrUnit, agreedSellerPricePrUnit, currencyType } = contract.contractProduct;
  const agreedPricePrUnit =
    getContractTradeType(contract, company) === ContractTradeTypeEnum.BUY
      ? agreedBuyerPricePrUnit
      : agreedSellerPricePrUnit;
  return agreedPricePrUnit[currencyType];
};

export type PaymentDirection = "receives" | "pays";

// Contracts where the payment goes the other way, from the seller to the buyer, are saved with negative prices.
export const isPaymentReceiverInverted = (agreedSellerPricePrUnit: number, agreedBuyerPricePrUnit: number) =>
  agreedSellerPricePrUnit < 0 || agreedBuyerPricePrUnit < 0;

// The seller receives and the buyer pays, unless the payment goes the other way.
export const getPaymentDirection = (contractTradeType: ContractTradeType, isInverted: boolean): PaymentDirection =>
  (contractTradeType === ContractTradeTypeEnum.SELL) !== isInverted ? "receives" : "pays";

/**
 * Whether the company receives or pays on the contract, and how much per price unit. The price is always positive,
 * since the direction carries the sign.
 */
export const getPaymentForCompany = (contract: Contract | ContractLight, company: CurrentCompany | undefined) => {
  const { agreedBuyerPricePrUnit, agreedSellerPricePrUnit, currencyType } = contract.contractProduct;
  return {
    direction: getPaymentDirection(
      getContractTradeType(contract, company),
      isPaymentReceiverInverted(agreedSellerPricePrUnit[currencyType], agreedBuyerPricePrUnit[currencyType])
    ),
    price: Math.abs(getAgreedPriceForCompany(contract, company)),
  };
};

export const getContractProgressPercent = (contract: Contract | ContractLight) => {
  const progress = 100 - contract.remainingAmount;
  return progress > 100 ? 100 : progress;
};

export const calculateBuyerPrice = (
  buyPrice: string,
  selectedServices: number[],
  paymentOptions: PlatformService[] | undefined,
  priceWeightUnit: number,
  companySpecificProduct: CompanySpecificProduct,
  isPaymentReceiverInverted: boolean,
  dueDateDays?: number | null
) => {
  const additionalServices = paymentOptions?.filter((setting) => selectedServices.includes(setting.id));

  const fixedServices = additionalServices?.filter(
    (s) => s.platformServiceUnit.code !== PlatformServiceUnitEnum.PROSENT
  );
  const percentServices = additionalServices?.filter(
    (s) => s.platformServiceUnit.code === PlatformServiceUnitEnum.PROSENT
  );

  const baseBuyPrice = parseFloat(buyPrice);

  // Always calculate fees positively first (as if adding)
  const priceWithFixed =
    fixedServices?.reduce((acc, setting) => {
      const _priceOrPercent = setting?.priceOrPercent ? setting.priceOrPercent : 0;

      if (setting.platformServiceUnit.code === PlatformServiceUnitEnum.TONN) {
        if (priceWeightUnit === PriceWeightUnitEnum.KG) {
          return acc + _priceOrPercent * 0.001;
        } else if (priceWeightUnit === PriceWeightUnitEnum.TONN) {
          return acc + _priceOrPercent;
        } else if (priceWeightUnit === PriceWeightUnitEnum.METER) {
          return acc + (_priceOrPercent * companySpecificProduct.kgPerMeter!) / 1000;
        } else if (priceWeightUnit === PriceWeightUnitEnum.STK) {
          return acc + (_priceOrPercent * companySpecificProduct.kgPerStk!) / 1000;
        } else if (priceWeightUnit === PriceWeightUnitEnum.M3) {
          return acc + (_priceOrPercent * companySpecificProduct.kgPerM3!) / 1000;
        }
      } else if (setting.platformServiceUnit.code === PlatformServiceUnitEnum.KG) {
        if (priceWeightUnit === PriceWeightUnitEnum.KG) {
          return acc + _priceOrPercent * 0.001;
        } else if (priceWeightUnit === PriceWeightUnitEnum.TONN) {
          return acc + _priceOrPercent;
        } else if (priceWeightUnit === PriceWeightUnitEnum.METER) {
          return acc + _priceOrPercent * companySpecificProduct.kgPerMeter!;
        } else if (priceWeightUnit === PriceWeightUnitEnum.STK) {
          return acc + _priceOrPercent * companySpecificProduct.kgPerStk!;
        } else if (priceWeightUnit === PriceWeightUnitEnum.M3) {
          return acc + _priceOrPercent * companySpecificProduct.kgPerM3!;
        }
      }
      return acc;
    }, baseBuyPrice) ?? baseBuyPrice;

  // Apply percentage-based services on top of the total (base + fixed)
  const priceWithAll =
    percentServices?.reduce((acc, setting) => {
      const _priceOrPercent = setting?.priceOrPercent ? setting.priceOrPercent : 0;

      // Apply dueDate multiplier: <=30 = 1x, 30-60 = 2x, 60-90 = 3x
      let dueDateMultiplier = 1;
      if (dueDateDays !== null && dueDateDays !== undefined) {
        if (dueDateDays > 30 && dueDateDays <= 60) {
          dueDateMultiplier = 2;
        } else if (dueDateDays > 60 && dueDateDays <= 90) {
          dueDateMultiplier = 3;
        }
      }
      const adjustedPercentage = _priceOrPercent * dueDateMultiplier;
      const multiplier = 1 + adjustedPercentage / 100;
      return acc * multiplier;
    }, priceWithFixed) ?? priceWithFixed;

  // The total fee is the same regardless of direction, only the sign changes
  const totalFee = priceWithAll - baseBuyPrice;
  const total = isPaymentReceiverInverted ? baseBuyPrice - totalFee : baseBuyPrice + totalFee;

  return total ? total.toFixed(2) : "0.00";
};

export const calculateTotalPrice = (buyPrice: string, amount: number) => {
  const priceConverted = parseFloat(buyPrice);
  if (isNaN(priceConverted) || isNaN(amount)) {
    return "0.00";
  }

  const priceIncWeight = parseFloat(buyPrice) * amount;
  return priceIncWeight.toFixed(2);
};

// Matched on the unit name ("kg", "tonn"), which is the same in every place a Unit comes from.
// Without a product only the weight units (kg, tonn) can be converted.
export const getKgPerUnit = (unit: string, product?: CompanySpecificProduct): number | null => {
  switch (unit.toLowerCase()) {
    case "kg":
      return 1;
    case "tonn":
      return 1000;
    case "stk":
      return product?.kgPerStk || null;
    case "m3":
      return product?.kgPerM3 || null;
    case "meter":
      return product?.kgPerMeter || null;
    default:
      return null;
  }
};

/**
 * Converts an amount to another unit, e.g. a contract amount in tonn to the kg its price is given per.
 * Returns null when the units can't be converted, such as stk on a product without a weight per piece.
 */
export const convertAmountBetweenUnits = (
  amount: number,
  fromUnit: string,
  toUnit: string,
  product: CompanySpecificProduct
): number | null => {
  if (fromUnit.toLowerCase() === toUnit.toLowerCase()) return amount;

  const fromKgPerUnit = getKgPerUnit(fromUnit, product);
  const toKgPerUnit = getKgPerUnit(toUnit, product);
  if (fromKgPerUnit === null || toKgPerUnit === null) return null;

  return (amount * fromKgPerUnit) / toKgPerUnit;
};

export const translateAmount = (amountUnitId: number, amount: string): string => {
  const amountNum = parseFloat(amount as unknown as string);
  if (Number.isNaN(amountNum)) return amount;
  switch (amountUnitId) {
    case 1: // kg
      return amountNum.toString();
    case 2: // tonn
      // convert tonnes to kg
      return (amountNum / 1000).toString();
    case 3: // stk
    case 4: // meter
    case 5: // m3
      return amountNum.toString();
    default:
      return amountNum.toString();
  }
};

/*
 * Intervals can be found on database "application" in table 'invoiceduedates'
 * */
export const getPaymentIntervalDaysById = (paymentIntervalId: number) => {
  switch (paymentIntervalId) {
    case 1:
      return 7;
    case 2:
      return 14;
    case 3:
      return 30;
    case 4:
      return 60;
    case 6:
      return 90;
    default:
      return 7;
  }
};
